<template>
  <v-app id="app">
    <HomeLoadingScreen v-if="loadingScreen" key="loading-screen" class="tw-page-content" />

    <confirm-dialog ref="confirm" class="overall" />
    <AppAlertDisplayer ref="alert" class="overall" />
    <notification-dialog ref="notification" class="overall" />
    <global-snackbar ref="snackbar" class="overall" />
    <v-main>
      <template v-if="!loadingScreen">
        <menu-drawer v-if="isFramedPage && authReady" />
        <section class="content-container p-relative">
          <top-bar v-if="isFramedPage && authReady" class="top-bar" />
          <v-slide-x-transition hide-on-leave>
            <div
              id="page"
              class="content-area tw-flex tw-flex-col tw-items-center"
              :class="{
                'content-area--framed': isFramedPage,
                small: $vuetify.breakpoint.smAndDown,
                'full-page-content': isGuidedAttendance,
              }"
            >
              <div class="tw-flex tw-min-h-full tw-w-full tw-justify-center sm:tw-flex-wrap">
                <router-view :key="$route.fullPath" class="tw-min-h-full tw-w-full tw-max-w-full" />
              </div>
            </div>
          </v-slide-x-transition>

          <VersionChecker v-if="isFramedPage && authReady" />
        </section>
        <survey-dialog v-if="isAcceptedTerms && authReady" />
      </template>
    </v-main>
  </v-app>
</template>

<script>
import HomeLoadingScreen from '@/v2/components/home/HomeLoadingScreen.vue'
import MenuDrawer from '@/v2/components/menu-drawer/MenuDrawer.vue'
import { extend, localize } from 'vee-validate'
import pt_BR from 'vee-validate/dist/locale/pt_BR.json'
import { email, required, max, alpha_num, alpha_spaces } from 'vee-validate/dist/rules'
import { createNamespacedHelpers } from 'vuex'
import ConfirmDialog from './components/ConfirmDialog.vue'
import GlobalSnackbar from './components/GlobalSnackBar.vue'
import NotificationDialog from './components/NotificationDialog.vue'
import SurveyDialog from './components/SurveyDialog.vue'
import TopBar from './components/TopBar.vue'
import { createRgbVarsForThemes } from './plugins/vuetify'
import { useAuthStore } from './store/pinia/auth'
import { useInactivityStore } from './store/pinia/inactivity'
import { isRequestAborted } from './utils/error'
import { isNil } from 'lodash'
import { usePhysicianStore } from './store/pinia/physician'
import AppAlertDisplayer from './v2/components/ui/alert/AppAlertDisplayer.vue'
import { useVersionStore } from './store/pinia/version'
import VersionChecker from './v2/components/version/VersionChecker.vue'

localize('pt_BR', pt_BR)
extend('required', {
  ...required,
  message: 'Campo obrigatório',
})
extend('not_numeric', {
  validate(value) {
    if (typeof value !== 'string') {
      return false
    }

    if (/^\d+$/.test(value)) {
      return false
    }

    return true
  },
  message: 'O campo não deve conter apenas números',
})
extend('email', {
  ...email,
  message: 'Deve ser um email válido',
})
extend('max_files', {
  validate(value, { length }) {
    if (isNil(value)) {
      return true
    }

    if (!Array.isArray(value)) {
      return false
    }

    return value.length <= length
  },
  params: ['length'],
  message: 'O número máximo de arquivos é {length}',
})
extend('max', {
  ...max,
  message: 'O campo não pode ter mais que {length} caracteres',
})

const {
  mapGetters: authGettersMap,
  mapActions: authActionsMap,
  mapMutations: authMutationsMap,
} = createNamespacedHelpers('auth')

export default {
  components: {
    MenuDrawer,
    TopBar,
    VersionChecker,
    ConfirmDialog,
    SurveyDialog,
    NotificationDialog,
    GlobalSnackbar,
    AppAlertDisplayer,
    HomeLoadingScreen,
  },
  name: 'App',
  computed: {
    ...authGettersMap(['isAcceptedTerms', 'userId', 'isLogged', 'token', 'pharmaceuticalId']),
    isFramedPage() {
      return this.$route.path !== '/' && !this.$route.meta.singlePage
    },
    authReady() {
      return this.authStore.hasUserBeenDefined && this.authStore.initiated
    },
    isAdmin() {
      return this.authStore.isAdmin
    },
    isGuidedAttendance() {
      return this.$route.path.includes('guided-attendance')
    },
  },
  data() {
    return {
      loadingScreen: false,
      ready: false,
    }
  },
  methods: {
    ...authActionsMap(['clearCredentials']),
    getAlerter(type) {
      const alertComponent = this.$refs.alert

      const alertFunctions = {
        ok({ message, description, action, icon }) {
          alertComponent.show('success', {
            message,
            description,
            action,
            icon,
          })
        },
        info({ message, description, action, icon }) {
          console.info(message)
          alertComponent.show('info', {
            message,
            description,
            action,
            icon,
          })
        },
        warn({ message, description, action, icon }) {
          console.warn(message)
          alertComponent.show('warning', {
            message,
            description,
            action,
            icon,
          })
        },
        error({ error, message, description, action, icon }) {
          if (error) {
            console.error(error)
          }

          if (isRequestAborted(error)) {
            return
          }

          message = message ?? error?.message
          description = description ?? error?.description
          // if (isUnauthorized(error)) {
          //   message = error.message
          //   description = error.description
          // }

          alertComponent.show('error', {
            message,
            description,
            action,
            icon,
          })
        },
      }

      return alertFunctions[type]
    },

    async checkUserHasAcceptedLatestTerms() {
      this.$api.getParameter()
    },

    async handleOldSession() {
      const oldRawUser = localStorage.getItem('kintsu-user')
      const session = JSON.parse(localStorage.getItem('kintsu-session'))
      if (!oldRawUser && !session?.pharmaceuticalId) {
        return
      }

      localStorage.clear()
      setTimeout(() => {
        const url = new URL('/login', window.location.origin)
        window.location.href = url.toString()
      }, 100)
    },
  },
  watch: {
    '$route.path'(value) {
      if (this.isFramedPage && !this.authStore.initiated) {
        this.loadingScreen = true
        setTimeout(() => {
          this.loadingScreen = false
          this.authStore.setInitiated(true)
        }, 2000)
      }
    },
  },
  setup() {
    const authStore = useAuthStore()
    const inactivityStore = useInactivityStore()
    const physicianStore = usePhysicianStore()
    const versionStore = useVersionStore()

    return {
      authStore,
      inactivityStore,
      physicianStore,
      versionStore,
    }
  },
  mounted() {
    this.handleOldSession()
    this.$root.alertComponent = this.$refs.alert
    this.$root.confirmComponent = this.$refs.confirm
    this.$root.notificationComponent = this.$refs.notification
    this.$root.snackbarComponent = this.$refs.snackbar
    window.document.title = 'Bioplanner'
    createRgbVarsForThemes(this.$vuetify.theme.themes)

    this.$eventBus.on('alert:info', this.getAlerter('info'))
    this.$eventBus.on('alert:error', this.getAlerter('error'))
    this.$eventBus.on('alert:warn', this.getAlerter('warn'))
    this.$eventBus.on('alert:ok', this.getAlerter('ok'))
  },
  beforeDestroy() {
    this.$eventBus.off('alert:info', this.getAlerter('info'))
    this.$eventBus.off('alert:error', this.getAlerter('error'))
    this.$eventBus.off('alert:warn', this.getAlerter('warn'))
    this.$eventBus.off('alert:ok', this.getAlerter('ok'))
  },
}
</script>

<style scoped>
.v-main :deep(.v-main__wrap) {
  display: flex;
  height: 100%;
}

.content-container {
  overflow: auto;
  flex: 1;
}

.content-area {
  margin: auto;
  overflow-y: auto;
  height: 100%;
  width: 100%;
  max-height: 100vh;
  scroll-behavior: smooth;
}

.content-area::-webkit-scrollbar-thumb,
.content-area :deep(::-webkit-scrollbar-thumb) {
  background: #d5d7e2 !important;
}

.top-bar {
  width: 100%;
}

.content-area--framed {
  max-height: calc(100vh - var(--top-height));
  width: 100%;
  padding-inline: 16px;
  padding-bottom: 8px;
}

.content-area--framed.full-page-content {
  padding-inline: 0;
  overflow-y: hidden;
}

@media (max-width: 640px) {
  #app :deep(.v-application--wrap) {
    min-height: 100dvh;
  }

  .content-area--framed {
    -ms-max-height: calc(100vh - var(--top-height) - 68px);
    max-height: calc(100dvh - var(--top-height) - 68px);
  }
}

.overall {
  z-index: 999 !important;
}
.crud-view.small :deep(.col) {
  flex-basis: auto !important;
}
* :deep(.crud-view.small .col) {
  flex-basis: auto !important;
}
* :deep(.v-data-table__mobile-row__cell) {
  overflow-wrap: anywhere;
}
.small :deep(.v-form .col) {
  flex-basis: auto !important;
}
* :deep(.v-form.small .col) {
  flex-basis: auto !important;
}
:deep(.v-card--disabled .textarea--readonly) {
  pointer-events: all;
}
</style>
